import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { PdfViewer } from 'reactApp/ui/PdfViewer/PdfViewer';
import { fetchDocumentData } from 'reactApp/ui/ReactViewer/MobilePdfViewer/MobilePdfViewer.helpers';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { sendXray } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';

import styles from './MobilePdfViewer.css';

type MobilePdfViewerProps = {
    item: CloudItem;
    storage: EStorageType;
    wait?: boolean;
    onShow();
    onClose();
};

let lastTouch,
    continues,
    lastY = 0,
    lastX = 0;

export const MobilePdfViewer: FC<MobilePdfViewerProps> = ({ item, storage, wait = false, onShow = noop, onClose = noop }) => {
    const ref = useRef<ReactZoomPanPinchRef>(null);
    const componentRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);
    const [error, setError] = useState('');
    const [content, setContent] = useState<Uint8Array>(new Uint8Array());

    useEffect(() => {
        function updateScale() {
            if (!ref.current) {
                return;
            }

            setScale(ref.current.state.scale);
        }

        const interval = setInterval(updateScale, 300);

        return () => {
            clearInterval(interval);
        };
    }, [ref.current]);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const reset = () => ref.current?.resetTransform();

        (window as any).zoom = ref.current;

        window.addEventListener('resize', reset);

        return () => {
            window.removeEventListener('resize', reset);
        };
    }, [ref.current]);

    useEffect(() => {
        let cancel = false;
        if (!item || !storage || wait || content.length) {
            return;
        }

        fetchDocumentData({ item, storage })
            .then((content) => {
                if (cancel) {
                    return;
                }

                setContent(content);
            })
            .catch((error) => {
                setError(error.message);
            });

        return () => {
            cancel = true;
        };
    }, [item, storage, wait]);

    useEffect(() => {
        onShow();

        return () => {
            onClose();
        };
    }, [onShow, onClose]);

    useEffect(() => {
        sendXray(['viewer', 'pdf', 'open']);
    }, []);

    useEffect(() => {
        if (error) {
            sendXray(['viewer', 'pdf', 'error', error]);
        }
    }, [error]);

    const handleError = useCallback((error: string) => {
        if (error) {
            setError(error);
        }
    }, []);

    const handlePanningStop = useCallback(() => {
        lastTouch = undefined;
        continues = false;
        lastY = 0;
        lastX = 0;
    }, []);

    const handlePanning = useCallback(
        (ref: ReactZoomPanPinchRef, ev) => {
            if (!componentRef.current) {
                return;
            }

            const { scale, positionX, positionY } = ref.state;
            const { identifier, screenY, screenX } = ev?.changedTouches?.[0] || {};
            const { scrollTop, scrollHeight, offsetHeight } = componentRef.current;

            if (identifier === lastTouch && screenY && screenX) {
                const Xdif = screenX - lastX;
                const Ydif = screenY - lastY;
                if (Math.abs(Xdif) > 50 || Math.abs(Ydif) > 50 || continues) {
                    if ((scrollTop === 0 && Ydif > 0) || (scrollTop + offsetHeight >= scrollHeight && Ydif < 0)) {
                        ref.setTransform(positionX + Xdif, positionY + Ydif, scale, 0);
                    } else {
                        ref.setTransform(positionX + Xdif, positionY, scale, 0);
                        componentRef.current.scrollTo({
                            top: scrollTop - Ydif / scale,
                        });
                    }

                    continues = true;
                    lastX = screenX;
                    lastY = screenY;
                }
            }
            if (identifier !== lastTouch) {
                lastTouch = identifier;
                lastX = screenX;
                lastY = screenY;
            }
        },
        [componentRef.current]
    );

    return (
        <>
            {content.length && !wait && !error && (
                <TransformWrapper
                    initialScale={1}
                    maxScale={4}
                    minScale={1}
                    ref={ref}
                    wheel={{ disabled: true }}
                    panning={{
                        disabled: scale <= 1,
                        velocityDisabled: true,
                        lockAxisY: true,
                    }}
                    onPanning={handlePanning}
                    onPanningStop={handlePanningStop}
                    doubleClick={{ step: 2, mode: scale <= 1 ? 'zoomIn' : 'reset' }}
                >
                    <TransformComponent wrapperClass={styles.zoomWrapper} contentClass={styles.zoomContent}>
                        <div className={styles.viewerWrapper}>
                            <PdfViewer
                                zoomScale={scale}
                                saveLoaded={scale > 1}
                                ref={componentRef}
                                content={content}
                                onError={handleError}
                                mobile
                            />
                        </div>
                    </TransformComponent>
                </TransformWrapper>
            )}
            {error && <div className={styles.text}>{error}</div>}
            {((!content.length && !error) || wait) && <Spinner />}
        </>
    );
};
